import type {
  AiChatAgentStatus,
  ChatSessionReplayPart,
  ChatSessionTraceItem,
  ReplaySourceBlock,
} from '@/api/ai-chat/types';

import type { ChatAttachment } from '../service/types';
import { randomIdSuffix } from './parse';
import type { CustomerSessionPreview } from './sessionPath';

export type SessionMessageType =
  | 'user'
  | 'assistant'
  | 'loading'
  | 'taskFinished'
  | 'taskTimeout'
  | 'taskDelegated';

export type ChatMessageStatus = 'pending' | 'streaming' | 'done' | 'error';

/** 助手一轮里的一个产物文件。字段来自 attachment 事件 text 里的 JSON。 */
export interface AssistantArtifact {
  /** 条目类型，协议里是 attachment。 */
  type?: string;
  url: string;
  name: string;
  mime_type: string;
  size: number;
}

/** 培训单选题的一项。`id` 不展示，发送时用 `label`。 */
export interface ClarificationOption {
  id: string;
  label: string;
  action?: string;
  recommended?: boolean;
}

/** 同一轮里的一道单选题。选项只在输入区出现。题目只在这些选项还在时留在气泡里。 */
export interface SessionClarification {
  message: string;
  questions: string[];
  options: ClarificationOption[];
}

export interface TrainingProposalChange {
  content_type: string;
  operation: string;
  target_ref: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

export interface ProposalActionOption {
  action: string;
  label: string;
  /** `provide_proposal_feedback` 时写入输入框的 placeholder。没有则用 label。 */
  content?: string;
}

/**
 * 本轮改稿。直播里 status 来自 proposal / proposal_status。
 * accepted，或 selected_action 为 direct_accept，都收成已采纳。
 * 历史加载会再查提案，用同一套字段。只有 locked 不算采纳成功。
 * 404 且 PROPOSAL_NOT_FOUND 时看下一轮用户原文，结果记在 selectedAction / locked。
 * 选项只在输入区，且只在这条消息后面还没有下一轮、也还没锁定时出现。
 */
export interface SessionProposal {
  proposalId: string;
  status: string;
  changes: TrainingProposalChange[];
  options: ProposalActionOption[];
  /** 已选的 action。accepted 时是 direct_accept。 */
  selectedAction?: string;
  /** 已采纳、已反馈，或后面还有别的用户消息。输入区不再给选项。 */
  locked?: boolean;
  accepting?: boolean;
  acceptError?: string;
  /** CONTENT_CHANGE_INCOMPLETE：可再点采纳。 */
  retryable?: boolean;
  /** CONTENT_CONFLICT / PROPOSAL_STALE / PROPOSAL_NOT_FOUND：不可再点。 */
  disabled?: boolean;
  /** CONTENT_STATE_INCONSISTENT：锁死。 */
  blocked?: boolean;
}

export interface SessionProposalStatus {
  proposalId: string;
  status: string;
  message: string;
}

/** 单条会话内容：固定 type / content / roundNumber，其余为渲染所需最小扩展。 */
export interface SessionMessage {
  id: string;
  type: SessionMessageType;
  content: string;
  roundNumber: number;
  createdAt: string;
  status?: ChatMessageStatus;
  /** 已被 progress 封口的过程文字，Think 区按行展示。 */
  processBlocks?: string[];
  progress?: string;
  /** 推理片段和工具步骤的到达顺序。有工具时思考区按这个渲染。 */
  trace?: ChatSessionTraceItem[];
  /** 用户消息附带的文件。展示在正文上方，不写入 content。 */
  attachments?: ChatAttachment[];
  /**
   * 本轮引用的客服会话。刚发出时是输入框卡片的快照；
   * 历史从 request.source_ref 解析，通常只有 sessionId 和可选的 focusMessageId。
   */
  sourcePreview?: CustomerSessionPreview;
  /**
   * 助手本轮产物。SSE `type=attachment` 的 text 是文件数组 JSON。
   * 多次事件合并成这一份，展示固定在该轮内容末尾。
   */
  artifacts?: AssistantArtifact[];
  /** 本轮按到达顺序追加的单选题。后面出现用户消息则整组视为已回答。 */
  clarifications?: SessionClarification[];
  proposal?: SessionProposal | null;
  proposalStatus?: SessionProposalStatus | null;
  /**
   * 无 replay 时的取消提示（单独一行，不拼进 content）。
   * 有 replay 时走 `{ kind: 'cancel' }` 块，不写这个字段。
   */
  cancelNotice?: string;
  /** 按 index 排好的可见块。历史和带块下标的 SSE 都写。没有它仍走固定分段。 */
  replay?: ChatSessionReplayPart[];
  /** 续写种子里的原始块。按 index 接后续 SSE，不参与渲染。 */
  blocks?: ReplaySourceBlock[];
  agentStatus?: AiChatAgentStatus;
  taskId?: string;
  taskTitle?: string;
  taskDuration?: string;
  viewed?: boolean;
  metadata?: Record<string, unknown>;
  /** 本轮耗时，单位秒。 */
  timeUsed?: number;
}

/** @deprecated 使用 SessionMessage；保留别名给轮询等既有接线。 */
export type ChatMessage = SessionMessage;

/**
 * 创建消息 ID
 * @param prefix 消息前缀（message-user / message-loading / session 等），分隔符为 `_`
 */
export const createMessageId = (prefix: string) =>
  `${prefix}_${randomIdSuffix()}`;

/** 用户或助手消息都算。只有详情 loading 占位时不算有对话。 */
export const hasConversationMessages = (messages: SessionMessage[] = []) => {
  if (messages.length === 0) return false;
  return !(messages.length === 1 && messages[0]?.type === 'loading');
};

export const getLatestRoundNumber = (messages: SessionMessage[]) =>
  messages.reduce((max, message) => Math.max(max, message.roundNumber || 0), 0);

/** 大于 80 轮给出轻提示；大于 98 轮禁止再输入。 */
export const SOFT_TURN_LIMIT = 80;
export const HARD_TURN_LIMIT = 98;

export const getConversationRoundCount = (messages: SessionMessage[] = []) => {
  if (!hasConversationMessages(messages)) return 0;
  return getLatestRoundNumber(messages);
};

export const isOverSoftTurnLimit = (messages: SessionMessage[] = []) =>
  getConversationRoundCount(messages) > SOFT_TURN_LIMIT;

export const isOverHardTurnLimit = (messages: SessionMessage[] = []) =>
  getConversationRoundCount(messages) > HARD_TURN_LIMIT;

export const getNextRoundNumber = (messages: SessionMessage[]) =>
  getLatestRoundNumber(messages) + 1;

export const createUserMessage = (
  content: string,
  roundNumber: number,
  attachments?: ChatAttachment[],
  sourcePreview?: CustomerSessionPreview
): SessionMessage => ({
  id: createMessageId('message-user'),
  type: 'user',
  content,
  roundNumber,
  createdAt: new Date().toISOString(),
  status: 'done',
  ...(attachments?.length ? { attachments } : {}),
  ...(sourcePreview ? { sourcePreview } : {}),
});

export const createLoadingMessage = (roundNumber: number): SessionMessage => ({
  id: createMessageId('message-loading'),
  type: 'loading',
  content: '',
  roundNumber,
  createdAt: new Date().toISOString(),
  status: 'pending',
});

export const withSessionRoundNumber = (
  message: SessionMessage,
  messages: SessionMessage[]
): SessionMessage => ({
  ...message,
  roundNumber:
    message.roundNumber > 0
      ? message.roundNumber
      : Math.max(getLatestRoundNumber(messages), 1),
});

const asAssistant = (
  message: SessionMessage,
  type?: SessionMessage['type']
): SessionMessage['type'] =>
  type ?? (message.type === 'loading' ? 'assistant' : message.type);

/** 把流式补丁合并进当前消息。loading 在补丁没给 type 时升成 assistant。 */
export const applyPatch = (
  current: SessionMessage,
  patch: Partial<SessionMessage>
): SessionMessage => ({
  ...current,
  ...patch,
  type: asAssistant(current, patch.type),
});

export interface OutgoingMessages {
  loading: SessionMessage;
  user: SessionMessage;
}

/** 空内容返回 null，调用方不发送。 */
export const buildOutgoingMessages = (
  content: string,
  messages: SessionMessage[],
  attachments?: ChatAttachment[],
  sourcePreview?: CustomerSessionPreview
): OutgoingMessages | null => {
  const normalized = content.trim();
  if (!normalized) return null;
  const roundNumber = getNextRoundNumber(messages);
  return {
    user: createUserMessage(
      normalized,
      roundNumber,
      attachments,
      sourcePreview
    ),
    loading: createLoadingMessage(roundNumber),
  };
};

export const createTaskResultMessage = (): SessionMessage => ({
  id: createMessageId('message-task-result'),
  type: 'loading',
  content: '',
  roundNumber: 0,
  createdAt: new Date().toISOString(),
  status: 'pending',
});

/** 从 createdAt 到现在的秒数。已有 timeUsed 时不覆盖。 */
const withSettledTimeUsed = (message: SessionMessage): SessionMessage => {
  if (typeof message.timeUsed === 'number') return message;
  const started = Date.parse(message.createdAt);
  if (!Number.isFinite(started)) return message;
  return {
    ...message,
    timeUsed: Math.max(0, Math.floor((Date.now() - started) / 1000)),
  };
};

export const settleMessage = (message: SessionMessage): SessionMessage =>
  withSettledTimeUsed({
    ...message,
    type: asAssistant(message),
    status: message.status === 'error' ? 'error' : 'done',
  });

export const failMessage = (
  message: SessionMessage,
  detail: string
): SessionMessage =>
  withSettledTimeUsed({
    ...message,
    type: asAssistant(message),
    content: `抱歉，当前服务暂时不可用：${detail}`,
    status: 'error',
  });

const USER_CANCELLED_TEXT = '用户已取消';

const withCancelReplay = (
  replay: SessionMessage['replay']
): NonNullable<SessionMessage['replay']> => {
  const parts = (replay ?? []).filter((part) => part.kind !== 'cancel');
  return [...parts, { kind: 'cancel', text: USER_CANCELLED_TEXT }];
};

/**
 * 用户点取消：不改正文，单独标取消行，status 收成 done。
 * 有 replay / blocks 时往 replay 追加 cancel 块；否则写 cancelNotice 走固定分段。
 */
export const abortMessage = (message: SessionMessage): SessionMessage => {
  const base = withSettledTimeUsed({
    ...message,
    type: asAssistant(message),
    status: 'done',
  });
  if (message.replay?.length || message.blocks?.length) {
    return { ...base, replay: withCancelReplay(message.replay) };
  }
  return { ...base, cancelNotice: USER_CANCELLED_TEXT };
};

export const markViewed = (message: SessionMessage): SessionMessage => ({
  ...message,
  viewed: true,
});
